import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	FormLabel,
	Grid,
	LinearProgress,
	Stack,
	Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import {
	useCreateBulkRenewalsMutation,
	usePreviewBulkRenewalsMutation,
} from '@/features/deals/dealsApi';
import { showToast } from '@/features/toast/toastSlice';
import { MAX_PER_API_BATCH_SIZE } from '@/utils/constants';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { OwnerAutocomplete } from '@/components/selects/OwnerSelect';
import { safelyParseDate } from '@/utils/date';
import ShowPreview from '@/components/dialogs/BulkRenewalsDialog/ShowPreview';
import InsightSelect from '@/components/selects/InsightSelect';
import FolioRadioGroup from '@/components/FolioRadioGroup';
import InsightFieldSelect from '@/components/selects/InsightFieldSelect';
import { PresetDateRangeSelect } from '@/components/selects/PresetDateRangeSelect';
import { useGridItemProps } from '@/components/table/filterGrid';
import { useFeatureFlagContext } from '@/context/featureFlagContext';

const toPayloadDate = (value) => (value ? format(value, 'yyyy-MM-dd') : null);

const getErrorMessage = (error, fallback) =>
	error?.data?.message ?? error?.error ?? error?.message ?? fallback;

const GROUP_TYPES = {
	client: 'client',
	policy: 'policy',
};

const GROUP_TYPE_ITEMS = Object.keys(GROUP_TYPES).map((k) => ({
	value: k,
	label: `One deal per ${k}`,
}));

export const BulkRenewalsDialog = ({ open, onClose }) => {
	const dispatch = useDispatch();

	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const canViewGroup = userDetails?.canViewGroup;

	const filters = useSelector((state) => state.renewalReportPrefs);

	const { generalGridProps } = useGridItemProps();

	const [ownerIds, setOwnerIds] = useState([]);
	const [branchIds, setBranchIds] = useState([]);
	const [endDateFrom, setEndDateFrom] = useState(null);
	const [endDateTo, setEndDateTo] = useState(null);
	const [groupType, setGroupType] = useState(GROUP_TYPES.client);

	const [preview, setPreview] = useState(null);
	const [portfolioTemplate, setPortfolioTemplate] = useState(null);

	const [previewBulkRenewals, previewMutation] = usePreviewBulkRenewalsMutation();
	const [createBulkRenewals, createMutation] = useCreateBulkRenewalsMutation();

	const { getFeatureFlags } = useFeatureFlagContext();
	const renewalReportAllOption = Boolean(getFeatureFlags('renewalReportAllOption')?.active);

	// Without renewalReportAllOption a branch is required, so an empty choice means the user's own
	const withRequiredBranch = (ids) =>
		ids.length === 0 && !renewalReportAllOption ? [userDetails?.broker?.branchId] : ids;

	useEffect(() => {
		if (open) {
			const ids = filters?.ownerIds ?? [];
			const start = safelyParseDate(filters?.fromDate);
			const end = safelyParseDate(filters?.toDate);
			setEndDateFrom(start);
			setEndDateTo(end);
			setOwnerIds(ids.length === 0 ? [userDetails?.userId] : ids);
			setBranchIds(withRequiredBranch(filters.branchIds));
			setPreview(null);
		}
	}, [
		open,
		filters?.fromDate,
		filters?.toDate,
		filters?.ownerIds,
		filters.branchIds,
		userDetails?.broker?.branchId,
		userDetails?.userId,
		renewalReportAllOption,
	]);

	const payload = useMemo(
		() => ({
			toDateFrom: toPayloadDate(endDateFrom),
			toDateTo: toPayloadDate(endDateTo),
			...(branchIds.length > 0 && { branchIds }),
			...((ownerIds ?? []).length > 0 && { ownerDocIds: ownerIds }),
			groupType,
		}),
		[endDateFrom, endDateTo, ownerIds, branchIds, groupType],
	);

	const canPreview = Boolean(payload.toDateFrom && payload.toDateTo);

	const canCreate =
		canPreview &&
		preview?.groups?.length > 0 &&
		!preview?.truncated &&
		!previewMutation.isLoading &&
		!createMutation.isLoading;

	const filtersDisabled = previewMutation.isLoading || createMutation.isLoading;

	const handlePreview = async () => {
		try {
			const response = await previewBulkRenewals(payload).unwrap();
			setPreview(response);
			if ((response?.groups ?? []).length === 0) {
				dispatch(
					showToast({
						message: 'No eligible renewals found for that range.',
						error: true,
					}),
				);
			}
		} catch (error) {
			dispatch(
				showToast({
					message: getErrorMessage(error, 'Could not preview renewals.'),
					error: true,
				}),
			);
		}
	};

	const handleCreate = async () => {
		try {
			const response = await createBulkRenewals({
				...payload,
				...(portfolioTemplate?.Id && { portfolioTemplateId: portfolioTemplate.Id }),
			}).unwrap();

			const createdCount = response?.created?.length ?? 0;
			const failedCount = response?.failed?.length ?? 0;

			dispatch(
				showToast({
					message:
						failedCount > 0
							? `${createdCount} renewals created, ${failedCount} failed.`
							: `${createdCount} renewals created.`,
					error: failedCount > 0,
					success: failedCount === 0,
				}),
			);
			setPreview(null);
		} catch (error) {
			dispatch(
				showToast({
					message: getErrorMessage(error, 'Could not create renewals.'),
					error: true,
				}),
			);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
			<DialogTitle component={Stack}>
				<Typography variant='inherit' sx={{ mb: 1 }}>
					Create bulk renewals
				</Typography>
				<Stack spacing={2} justifyContent={'center'}>
					<Typography>
						A maximum of {MAX_PER_API_BATCH_SIZE} renewal deals can be created at one
						time. Please run outside of work hours to limit the impact on ledger
						performance.
					</Typography>

					<Stack direction='column' spacing={1} width='100%'>
						<FolioRadioGroup
							value={groupType}
							onChange={(e) => {
								setGroupType(e.target.value);
								setPreview(null);
							}}
							items={GROUP_TYPE_ITEMS}
						/>
					</Stack>

					<Grid
						container
						spacing={1}
						alignItems='flex-end'
						sx={{
							mt: 1,
							width: '100%',
							'& .MuiInputBase-root': {
								height: 40,
								minHeight: 40,
							},
							'& .MuiOutlinedInput-input': {
								padding: '8px 14px',
								backgroundColor: 'transparent !important',
							},
							'& .MuiFormControl-root': {
								width: '100%',
							},
						}}
					>
						{canViewGroup && (
							<>
								<Grid {...generalGridProps}>
									<FormControl fullWidth>
										<FormLabel>Client Owner</FormLabel>
										<OwnerAutocomplete
											includeInactive
											multiple
											userId={ownerIds}
											disabled={!userDetails?.canViewGroup || filtersDisabled}
											onChange={(newUsers) => {
												setOwnerIds(newUsers);
												setPreview(null);
											}}
											returnValue='id'
										/>
									</FormControl>
								</Grid>
								<Grid {...generalGridProps}>
									<FormControl fullWidth>
										<FormLabel>Branch</FormLabel>
										<InsightFieldSelect
											multiple
											value={branchIds}
											onChange={(nextBranchIds) => {
												setBranchIds(withRequiredBranch(nextBranchIds));
												setPreview(null);
											}}
											fieldType={'Branch'}
											disabled={filtersDisabled}
											clearable={renewalReportAllOption}
											showAllOption
										/>
									</FormControl>
								</Grid>
							</>
						)}

						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>End date</FormLabel>
								<PresetDateRangeSelect
									disabled={filtersDisabled}
									from={endDateFrom ? new Date(endDateFrom) : null}
									to={endDateTo ? new Date(endDateTo) : null}
									onChange={(nextFrom, nextTo) => {
										setEndDateFrom(nextFrom);
										setEndDateTo(nextTo);
										setPreview(null);
									}}
								/>
							</FormControl>
						</Grid>

						<Grid size='auto'>
							<Button
								variant='contained'
								onClick={handlePreview}
								disabled={
									!canPreview ||
									previewMutation.isLoading ||
									createMutation.isLoading
								}
							>
								{previewMutation.isLoading ? (
									<CircularProgress size={18} px={2} color='inherit' />
								) : (
									'Preview'
								)}
							</Button>
						</Grid>
					</Grid>
				</Stack>
			</DialogTitle>
			<DialogContent sx={{ p: 3 }}>
				<Stack spacing={2.5}>
					{(previewMutation.isLoading || createMutation.isLoading) && <LinearProgress />}

					{preview?.truncated && (
						<Alert severity='warning'>
							This range matched more policy pages than can be safely processed in one
							run.
						</Alert>
					)}

					{preview && <ShowPreview {...{ preview }} />}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ backgroundColor: 'transparent', p: 0 }}>
				<Stack sx={{ width: '100%' }}>
					{preview && (
						<Box sx={{ width: '50%', py: 1, px: 3 }}>
							<FormControl fullWidth>
								<FormLabel>Portfolio template</FormLabel>
								<InsightSelect
									endpoint={'/ClientInsurancePortfolio/List'}
									value={portfolioTemplate}
									clearable={true}
									onChange={(v) => {
										setPortfolioTemplate(
											v
												? {
														Name: v.Name,
														Id: v.Id,
													}
												: null,
										);
									}}
								/>
								<FormHelperText>
									Choose the template for generating the Client Insurance
									Portfolio
								</FormHelperText>
							</FormControl>
						</Box>
					)}
					<Stack
						direction='row'
						sx={(theme) => ({
							backgroundColor:
								theme.palette.mode === 'dark'
									? theme.palette.darkModeTextField
									: theme.palette.dialogActions,
							p: 1.5,
							justifyContent: 'flex-end',
						})}
					>
						<Button
							onClick={onClose}
							disabled={previewMutation.isLoading || createMutation.isLoading}
						>
							Cancel
						</Button>
						<Button variant='contained' onClick={handleCreate} disabled={!canCreate}>
							{createMutation.isLoading ? (
								<CircularProgress size={18} color='inherit' />
							) : preview?.groups?.length ? (
								`Create ${preview?.groups?.length ?? 0} renewals`
							) : (
								'Create renewals'
							)}
						</Button>
					</Stack>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};
